import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

const SceneContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #fff;
  padding: 2rem;
`;

const SceneTitle = styled.h2`
  font-size: 3rem;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin: 0 0 1rem 0;
  text-align: center;
  background: linear-gradient(45deg, #fff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(0, 255, 136, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  color: #00ff88;
  text-align: center;
  margin: 0 0 3rem 0;
  font-weight: 300;
  letter-spacing: 2px;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const QuizPlatform = styled.div`
  width: 600px;
  height: 400px;
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border: 3px solid #00ff88;
  border-radius: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 20px;
    background: linear-gradient(45deg, #00ff88, #00cc6a);
    opacity: 0.2;
    filter: blur(20px);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.2; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.02); }
  }
  
  @media (max-width: 768px) {
    width: 90%;
    height: 300px;
  }
`;

const QuizContent = styled.div`
  text-align: center;
  z-index: 2;
  
  .quiz-icon {
    font-size: 4rem;
    margin-bottom: 2rem;
    filter: drop-shadow(0 0 20px rgba(0, 255, 136, 0.5));
    animation: float 3s ease-in-out infinite;
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
  }
  
  .quiz-text {
    font-size: 1.2rem;
    color: #ccc;
    margin: 0 0 2rem 0;
    line-height: 1.6;
    max-width: 400px;
  }
`;

const TakeQuizButton = styled.button`
  padding: 1rem 2.5rem;
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000;
  border: none;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 255, 136, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(0, 255, 136, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  
  .floating-element {
    position: absolute;
    width: 60px;
    height: 60px;
    background: linear-gradient(45deg, #00ff88, #00cc6a);
    border-radius: 50%;
    opacity: 0.1;
    animation: float-element 6s ease-in-out infinite;
    
    &:nth-child(1) {
      top: 20%;
      left: 10%;
      animation-delay: 0s;
    }
    
    &:nth-child(2) {
      top: 60%;
      right: 15%;
      animation-delay: 2s;
    }
    
    &:nth-child(3) {
      bottom: 20%;
      left: 20%;
      animation-delay: 4s;
    }
    
    &:nth-child(4) {
      top: 40%;
      right: 25%;
      animation-delay: 1s;
    }
    
    &:nth-child(5) {
      bottom: 40%;
      right: 30%;
      animation-delay: 3s;
    }
  }
  
  @keyframes float-element {
    0%, 100% { transform: translateY(0px) scale(1); opacity: 0.1; }
    50% { transform: translateY(-20px) scale(1.1); opacity: 0.2; }
  }
`;

const BottomSection = styled.div`
  text-align: center;
  margin-top: 2rem;
  
  .bottom-title {
    font-size: 2.5rem;
    color: #00ff88;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 0 0 1rem 0;
    text-shadow: 0 0 20px rgba(0, 255, 136, 0.5);
  }
  
  .bottom-subtitle {
    font-size: 1.1rem;
    color: #ccc;
    margin: 0;
    font-weight: 300;
    letter-spacing: 1px;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    .bottom-title {
      font-size: 2rem;
    }
  }
`;

const SpriteLogo = styled.div`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #000;
  font-weight: bold;
  box-shadow: 0 0 30px rgba(0, 255, 136, 0.3);
  animation: pulse 2s ease-in-out infinite;
  
  &::before {
    content: '🥤';
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  
  @media (max-width: 768px) {
    position: relative;
    bottom: auto;
    right: auto;
    margin-top: 2rem;
  }
`;

const QuizScene: React.FC = () => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [isQuizStarted, setIsQuizStarted] = useState(false);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Animate quiz content on scene enter
    gsap.fromTo('.quiz-content', 
      { 
        opacity: 0, 
        y: 50,
        scale: 0.9
      },
      { 
        opacity: 1, 
        y: 0,
        scale: 1,
        duration: 1.2,
        ease: "power2.out"
      }
    );

    // Animate floating elements
    gsap.fromTo('.floating-element', 
      { 
        opacity: 0, 
        scale: 0,
        rotation: -180
      },
      { 
        opacity: 0.1, 
        scale: 1,
        rotation: 0,
        duration: 1.5,
        ease: "back.out(1.7)",
        stagger: 0.2
      }
    );

  }, []);

  const handleTakeQuiz = () => {
    setIsQuizStarted(true);
    // Here you would typically navigate to the actual quiz or show quiz questions
    console.log('Quiz started!');
  };

  return (
    <SceneContainer ref={sceneRef} className="scene">
      <SceneTitle>Find Your Gift</SceneTitle>
      <Subtitle>Quiz Experience</Subtitle>
      
      <QuizPlatform>
        <FloatingElements>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
        </FloatingElements>
        
        <QuizContent className="quiz-content">
          <div className="quiz-icon">💡</div>
          <p className="quiz-text">
            Discover your unique talents and unlock your infinite potential through our interactive quiz experience.
          </p>
          <TakeQuizButton onClick={handleTakeQuiz}>
            Take The Quiz
          </TakeQuizButton>
        </QuizContent>
      </QuizPlatform>
      
      <BottomSection>
        <h3 className="bottom-title">Infinite Potential</h3>
        <p className="bottom-subtitle">In partnership with Wakanda Forever</p>
      </BottomSection>
      
      <SpriteLogo />
    </SceneContainer>
  );
};

export default QuizScene;
